/**
 * Clients List Page (Admin)
 * Display all clients with search, filter, and pagination
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAdminApi, type ClientListParams } from '../../../api/admin/clients.api';
import DataTable, { type Column } from '../../../components/ui/DataTable';
import Pagination from '../../../components/ui/Pagination';
import Badge from '../../../components/ui/Badge';

interface Client {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  status: 'active' | 'inactive' | 'suspended' | 'closed';
  creditBalance: number;
  createdAt: string;
  lastLoginAt?: string;
}

export default function ClientsListPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(25);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Load clients
  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ClientListParams = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortBy as any,
        sortOrder,
      };

      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter as any;

      const response = await clientsAdminApi.list(params);

      setClients(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [currentPage, sortBy, sortOrder, statusFilter]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadClients();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const handleSort = (key: string, order: 'asc' | 'desc') => {
    setSortBy(key);
    setSortOrder(order);
  };

  const handleRowClick = (client: Client) => {
    navigate(`/admin/clients/${client.id}`);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
      active: 'success',
      inactive: 'default',
      suspended: 'warning',
      closed: 'danger',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const columns: Column<Client>[] = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (client) => (
        <div>
          <div className="font-medium text-white">
            {client.firstName} {client.lastName}
          </div>
          {client.companyName && (
            <div className="text-xs text-slate-400">{client.companyName}</div>
          )}
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (client) => getStatusBadge(client.status),
    },
    {
      key: 'creditBalance',
      label: 'Credit Balance',
      render: (client) => (
        <span className={client.creditBalance > 0 ? 'text-green-400' : 'text-slate-400'}>
          ${client.creditBalance.toFixed(2)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (client) => new Date(client.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-slate-800/80 border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Clients</h1>
              <p className="text-slate-400 mt-1">Manage your clients and contacts</p>
            </div>
            <button
              onClick={() => navigate('/admin/clients/new')}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg font-medium"
            >
              + Add Client
            </button>
          </div>

          {/* Filters */}
          <div className="mt-6 flex items-center space-x-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-4 py-2 bg-gray-900 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-gray-900 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-300">
            {error}
          </div>
        )}

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <DataTable
            data={clients}
            columns={columns}
            keyField="id"
            loading={loading}
            emptyMessage="No clients found"
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRowClick={handleRowClick}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      </div>
    </div>
  );
}
